// Задачи: очередь → в работе → ждёт вас → готово (PLAN §2 «Доска задач»). Поставил «сделай разбор
// оффера X» — и ушёл: агент возьмёт задачу, когда будет место и лимит подписки позволит.
// Ниже — «Расписание и очередь» (бывшая вкладка «Расписание»): когда агент работает сам и по каким правилам.
import { Button, Chip, EmptyState, type Tone, toast, when } from '@autoaff/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ListTodo, MessageCircle, Plus } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import type { TaskView } from '../../agent-api';
import { api } from '../../api';
import { chatStore } from '../../chat/store';
import { ScheduleSection } from './Schedule';

const COLUMNS: Array<{ key: string; title: string; statuses: TaskView['status'][] }> = [
  { key: 'queue', title: 'В очереди', statuses: ['queued'] },
  { key: 'work', title: 'В работе', statuses: ['running'] },
  { key: 'owner', title: 'Ждёт вас', statuses: ['waiting_owner'] },
  { key: 'done', title: 'Готово', statuses: ['done', 'failed', 'cancelled'] },
];

const FINAL: Record<string, { text: string; tone: Tone }> = {
  done: { text: 'Готово', tone: 'success' },
  failed: { text: 'Не удалось', tone: 'danger' },
  cancelled: { text: 'Отменена', tone: 'default' },
};

function NewTask({ onDone }: { onDone: () => void }) {
  const [title, setTitle] = useState('');
  const [desc, setDesc] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (title.trim().length < 3) return;
    setBusy(true);
    try {
      await api('/api/agent/tasks', {
        title: title.trim(),
        ...(desc.trim() ? { description: desc.trim() } : {}),
      });
      setTitle('');
      setDesc('');
      toast('Задача в очереди — агент возьмёт её, как только освободится');
      onDone();
    } catch (err) {
      toast((err as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <form className="card task-new" onSubmit={submit}>
      <label className="sr-only" htmlFor="task-title">
        Что сделать
      </label>
      <input
        id="task-title"
        className="input"
        placeholder="Что сделать: «Разбери, почему упал апрув по органайзеру»"
        value={title}
        maxLength={200}
        onChange={(e) => setTitle(e.target.value)}
      />
      <label className="sr-only" htmlFor="task-desc">
        Подробности
      </label>
      <textarea
        id="task-desc"
        className="input"
        placeholder="Подробности, если нужны"
        rows={2}
        value={desc}
        maxLength={4000}
        onChange={(e) => setDesc(e.target.value)}
      />
      <Button
        type="submit"
        variant="primary"
        icon={busy ? <span className="spinner" /> : <Plus />}
        disabled={busy || title.trim().length < 3}
      >
        Поставить задачу
      </Button>
    </form>
  );
}

function TaskCard({ t, onChanged }: { t: TaskView; onChanged: () => void }) {
  const cancel = async () => {
    try {
      await api(`/api/agent/tasks/${t.id}/cancel`, {});
      onChanged();
    } catch (e) {
      toast((e as Error).message);
    }
  };
  const final = FINAL[t.status];
  return (
    <li className="task">
      <div className="task-top">
        <b>{t.title}</b>
        {t.createdBy === 'agent' && <Chip>от агента</Chip>}
        {final && <Chip tone={final.tone}>{final.text}</Chip>}
        {t.working && (
          <>
            <span className="status-dot busy" aria-hidden="true" />
            <span className="sr-only">работает</span>
          </>
        )}
      </div>
      {t.progress && t.status !== 'done' && <p className="task-progress">{t.progress}</p>}
      {t.result && t.status === 'done' && <p className="task-progress">{t.result.slice(0, 300)}</p>}
      <div className="task-foot">
        <span className="muted">{when(t.finishedAt ?? t.updatedAt)}</span>
        {t.threadId && (
          <Button
            size="sm"
            variant="ghost"
            icon={<MessageCircle />}
            onClick={() =>
              // Ждёт вас — разговор задачи с закреплённым вопросом: первое сообщение — ответ, агент продолжит задачу
              t.status === 'waiting_owner' && t.threadId
                ? chatStore.open({
                    ask: {
                      key: `task:${t.id}`,
                      threadId: t.threadId,
                      questions: [t.progress ?? t.title],
                      source: `Задача «${t.title}»`,
                      at: t.updatedAt,
                      task: true,
                    },
                  })
                : chatStore.open({ threadId: t.threadId })
            }
          >
            {t.status === 'waiting_owner' ? 'Ответить' : 'Открыть'}
          </Button>
        )}
        {(t.status === 'queued' || t.status === 'waiting_owner') && (
          <Button size="sm" variant="ghost" onClick={cancel}>
            Отменить
          </Button>
        )}
      </div>
    </li>
  );
}

export function TasksTab() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['agent', 'tasks'],
    queryFn: () => api<TaskView[]>('/api/agent/tasks'),
    refetchInterval: 15_000,
  });
  const refresh = () => void qc.invalidateQueries({ queryKey: ['agent', 'tasks'] });
  const items = q.data ?? [];
  return (
    <div className="pc">
      <NewTask onDone={refresh} />
      {q.isPending && <div className="card skeleton" style={{ height: 200 }} />}
      {q.isSuccess && items.length === 0 && (
        <div className="card">
          <EmptyState icon={<ListTodo />} title="Задач пока нет">
            Поставьте агенту задачу словами — он возьмёт её в работу отдельным запуском и отметит ход здесь.
            Если ему понадобится ваше решение, задача перейдёт в «Ждёт вас».
          </EmptyState>
        </div>
      )}
      {items.length > 0 && (
        <div className="task-board">
          {COLUMNS.map((c) => {
            const list = items.filter((t) => c.statuses.includes(t.status));
            return (
              <section key={c.key} className="task-col" aria-label={c.title}>
                <h2>
                  {c.title} <span className="count">{list.length}</span>
                </h2>
                <ul>
                  {list.map((t) => (
                    <TaskCard key={t.id} t={t} onChanged={refresh} />
                  ))}
                  {list.length === 0 && <li className="task-empty muted">Пусто</li>}
                </ul>
              </section>
            );
          })}
        </div>
      )}
      <ScheduleSection />
    </div>
  );
}
